import React, { useCallback, useEffect, useState } from 'react';
import { apiJson, resolvePublicStorageUrl } from '../../apiClient';
import { expenseCategoryLabelForUser } from '../../constants/factoryExpenseCategories';
import { expensePaymentLabel } from '../../constants/expensePayment';
import { staffPayTypeLabel } from '../../constants/staffPayTypes';
import { useLanguage } from '../../i18n/LanguageContext';
import {
  downloadExpensesCsv,
  downloadExpensesPdf,
  downloadTextFile,
  downloadUrlAsFile,
} from '../../utils/expenseExport';
import { ExpenseListShell } from './ExpenseListShell';
import './ExpensesShell.css';

function categoryBadgeClass(category) {
  const c = String(category || '').toLowerCase();
  if (c === 'boss') return 'expenses-cat expenses-cat--boss';
  if (c === 'staff') return 'expenses-cat expenses-cat--staff';
  if (c === 'office') return 'expenses-cat expenses-cat--office';
  return 'expenses-cat expenses-cat--other';
}

function formatExpenseDate(value, lang) {
  if (value == null || value === '') return '—';
  try {
    const d = new Date(`${value}T12:00:00`);
    if (Number.isNaN(d.getTime())) return String(value);
    const locale = lang === 'sw' ? 'sw-TZ' : undefined;
    return d.toLocaleDateString(locale, { dateStyle: 'medium' });
  } catch {
    return String(value);
  }
}

function hasEvidence(row) {
  return Boolean(row?.smsMessage?.trim() || row?.evidenceUrl);
}

export default function ExpenseCategoryList({ user, category, title, lede, onAddExpense }) {
  const { t, lang } = useLanguage();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewRow, setViewRow] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiJson(`/expenses?category=${encodeURIComponent(category)}`);
      setExpenses(Array.isArray(data.expenses) ? data.expenses : []);
    } catch (e) {
      setExpenses([]);
      setError(e.message || t('exp.list.loadError'));
    } finally {
      setLoading(false);
    }
  }, [category, t]);

  useEffect(() => {
    void load();
  }, [load]);

  let body;
  if (loading) {
    body = (
      <div className="expenses-empty">
        <p className="expenses-empty-text">{t('exp.list.loading')}</p>
      </div>
    );
  } else if (error) {
    body = (
      <div className="expenses-empty">
        <p className="expenses-empty-title">{t('exp.list.loadError')}</p>
        <p className="expenses-empty-text">{error}</p>
      </div>
    );
  } else if (expenses.length === 0) {
    body = (
      <div className="expenses-empty">
        <p className="expenses-empty-title">{t('exp.list.emptyTitle')}</p>
        <p className="expenses-empty-text">{t('exp.list.emptyText')}</p>
      </div>
    );
  } else {
    body = (
      <>
        <div className="expenses-record-cards" aria-label="Expense list">
          {expenses.map((row) => (
            <article key={row.id} className="expenses-record-card">
              <div className="expenses-record-card__top">
                <span className={categoryBadgeClass(category)}>{expenseCategoryLabelForUser(user, category, lang)}</span>
                <span className="expenses-record-card__amount">TZS {Number(row.amount).toLocaleString()}</span>
              </div>
              <h3 className="expenses-record-card__title">{row.description}</h3>
              {row.staffPayType ? (
                <p className="expenses-record-card__pay-type">
                  {staffPayTypeLabel(row.staffPayType, lang)}
                  {row.staffPayType === 'day_work' && row.cementBags != null ? (
                    <>
                      {' '}
                      · {t('exp.list.cementLine', {
                        bags: row.cementBags,
                        rate: Number(row.cementBagRateTzs || 0).toLocaleString(),
                      })}
                      {row.productionCementBags != null ? (
                        <span className="expenses-record-card__prod-ref">
                          {' '}
                          {t('exp.list.productionRef', { bags: row.productionCementBags })}
                        </span>
                      ) : null}
                    </>
                  ) : null}
                </p>
              ) : null}
              <dl className="expenses-record-card__meta">
                <div>
                  <dt>{t('exp.list.date')}</dt>
                  <dd>{formatExpenseDate(row.expenseDate, lang)}</dd>
                </div>
                <div>
                  <dt>{t('exp.list.by')}</dt>
                  <dd>{row.createdBy}</dd>
                </div>
                <div>
                  <dt>{t('exp.list.payment')}</dt>
                  <dd>{expensePaymentLabel(row.paymentMethod, lang)}</dd>
                </div>
                {row.paymentReference ? (
                  <div>
                    <dt>{t('exp.list.ref')}</dt>
                    <dd>{row.paymentReference}</dd>
                  </div>
                ) : null}
              </dl>
              {row.notes ? <p className="expenses-record-card__notes">{row.notes}</p> : null}
              {hasEvidence(row) ? (
                <button type="button" className="expenses-evidence-link" onClick={() => setViewRow(row)}>
                  {t('exp.list.viewEvidence')}
                </button>
              ) : null}
            </article>
          ))}
        </div>
        <div className="expenses-table-wrap expenses-table-wrap--desktop">
          <table className="expenses-data-table">
            <thead>
              <tr>
                <th>{t('exp.list.date')}</th>
                <th>{t('exp.list.description')}</th>
                <th>{t('exp.list.type')}</th>
                <th className="expenses-th--num">{t('exp.list.amount')}</th>
                <th>{t('exp.list.by')}</th>
                <th>{t('exp.list.payment')}</th>
                <th>{t('exp.list.ref')}</th>
                <th>{t('exp.list.notes')}</th>
                <th>{t('exp.list.evidence')}</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((row) => (
                <tr key={row.id}>
                  <td>{formatExpenseDate(row.expenseDate, lang)}</td>
                  <td>{row.description}</td>
                  <td className="expenses-td-notes">
                    {row.staffPayType ? staffPayTypeLabel(row.staffPayType, lang) : '—'}
                    {row.staffPayType === 'day_work' && row.cementBags != null
                      ? ` · ${row.cementBags}×${Number(row.cementBagRateTzs || 0).toLocaleString()}`
                      : ''}
                  </td>
                  <td className="expenses-td--num">{Number(row.amount).toLocaleString()}</td>
                  <td>{row.createdBy}</td>
                  <td>{expensePaymentLabel(row.paymentMethod, lang)}</td>
                  <td className="expenses-td-notes">{row.paymentReference || '—'}</td>
                  <td className="expenses-td-notes">{row.notes || '—'}</td>
                  <td>
                    {hasEvidence(row) ? (
                      <button type="button" className="expenses-evidence-link" onClick={() => setViewRow(row)}>
                        {t('exp.list.view')}
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  const evidenceSrc = viewRow ? resolvePublicStorageUrl(viewRow.evidenceUrl) : null;
  const evidenceIsPdf = evidenceSrc && /\.pdf(\?|$)/i.test(evidenceSrc);

  const exportOptions = () => {
    const catLabel = expenseCategoryLabelForUser(user, category, lang);
    return {
      title: t('exp.list.exportTitle', { category: catLabel }),
      subtitle: catLabel,
      categoryLabel: catLabel,
      includeCategory: false,
      lang,
    };
  };

  const handleDownloadCsv = () => {
    if (!expenses.length) return;
    downloadExpensesCsv(expenses, user, {
      ...exportOptions(),
      filename: `expenses_${category}_${new Date().toISOString().slice(0, 10)}.csv`,
    });
  };

  const handleDownloadPdf = async () => {
    if (!expenses.length || pdfBusy) return;
    setPdfBusy(true);
    try {
      await downloadExpensesPdf(expenses, user, exportOptions());
    } catch (e) {
      window.alert(e.message || t('exp.list.pdfError'));
    } finally {
      setPdfBusy(false);
    }
  };

  const handleDownloadEvidence = async () => {
    if (!evidenceSrc || !viewRow) return;
    setDownloading(true);
    try {
      const ext = evidenceIsPdf ? 'pdf' : 'jpg';
      await downloadUrlAsFile(evidenceSrc, `expense-${viewRow.id}-receipt.${ext}`);
    } catch (e) {
      window.alert(e.message || t('exp.list.downloadFailed'));
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadSms = () => {
    if (!viewRow?.smsMessage?.trim()) return;
    downloadTextFile(`expense-${viewRow.id}-sms.txt`, viewRow.smsMessage.trim());
  };

  return (
    <ExpenseListShell title={title} lede={lede} onAddExpense={onAddExpense}>
      <div className="expenses-list-meta">
        <span className={categoryBadgeClass(category)}>{expenseCategoryLabelForUser(user, category, lang)}</span>
        {!loading && !error ? (
          <span className="expenses-list-meta__count">{t('exp.list.records', { count: expenses.length })}</span>
        ) : null}
        {!loading && !error && expenses.length > 0 ? (
          <>
            <button type="button" className="expenses-btn expenses-btn--secondary" onClick={handleDownloadCsv}>
              {t('exp.export.csv')}
            </button>
            <button
              type="button"
              className="expenses-btn expenses-btn--secondary"
              disabled={pdfBusy}
              onClick={() => void handleDownloadPdf()}
            >
              {pdfBusy ? t('exp.export.exportingPdf') : t('exp.export.pdf')}
            </button>
          </>
        ) : null}
        {!loading ? (
          <button type="button" className="expenses-btn expenses-btn--secondary expenses-list-meta__refresh" onClick={() => void load()}>
            {t('exp.common.refresh')}
          </button>
        ) : null}
      </div>
      {body}

      {viewRow ? (
        <div className="expenses-modal-backdrop" role="presentation" onClick={() => setViewRow(null)}>
          <div className="expenses-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="expenses-modal__title">{viewRow.description}</h3>
            <p className="expenses-modal__meta">
              TZS {Number(viewRow.amount).toLocaleString()} · {formatExpenseDate(viewRow.expenseDate, lang)}
            </p>
            {viewRow.smsMessage?.trim() ? (
              <div className="expenses-modal__block">
                <div className="expenses-modal__block-head">
                  <h4>{t('exp.list.smsTitle')}</h4>
                  <button type="button" className="expenses-modal__dl" onClick={handleDownloadSms}>
                    {t('exp.list.downloadSms')}
                  </button>
                </div>
                <pre className="expenses-modal__sms">{viewRow.smsMessage.trim()}</pre>
              </div>
            ) : null}
            {evidenceSrc ? (
              <div className="expenses-modal__block">
                <div className="expenses-modal__block-head">
                  <h4>{t('exp.list.receiptTitle')}</h4>
                  <button
                    type="button"
                    className="expenses-modal__dl"
                    disabled={downloading}
                    onClick={() => void handleDownloadEvidence()}
                  >
                    {downloading ? t('exp.export.exportingCsv') : t('exp.list.downloadFile')}
                  </button>
                </div>
                {evidenceIsPdf ? (
                  <a href={evidenceSrc} target="_blank" rel="noopener noreferrer" className="expenses-modal__pdf-link">
                    {t('exp.list.openPdf')}
                  </a>
                ) : (
                  <a href={evidenceSrc} target="_blank" rel="noopener noreferrer">
                    <img src={evidenceSrc} alt={t('exp.list.evidenceAlt')} className="expenses-modal__img" />
                  </a>
                )}
              </div>
            ) : null}
            <div className="expenses-modal__actions">
              <button type="button" className="expenses-btn expenses-btn--secondary" onClick={() => setViewRow(null)}>
                {t('exp.list.close')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </ExpenseListShell>
  );
}
